import { IClassListItem } from "../../model/respone/classes/IClassListItem";
import { IFinancialSummary } from "../../model/respone/financial/IFinancialSummary";

/**
 * Hàm format thuần dùng chung cho cả bản desktop (ClassesPage) lẫn bản mobile.
 * Tách khỏi ClassesPage để 2 nhánh render không lặp code — logic giữ nguyên 100%.
 */

/**
 * 1 học phần (môn) gom từ nhiều lớp tín chỉ — khớp shape của memo `subjects` trong ClassesPage.
 * Dùng làm kiểu prop cho `SubjectCard` bản mobile.
 */
export interface ISubjectGroup {
    /** Khóa group + định danh dòng học phần = `${id_mon}|${so_tin_chi}`.
     *  1 id_mon có thể có nhiều số tín (môn ở các CTDT khác nhau) → phải tách dòng. */
    subjectKey: string;
    id_mon: number;
    ten_mon: string;
    ky_hieu: string;
    ky_thu: number | undefined;
    nhom_tu_chon_text: string | undefined;
    nhom_TuChon_Chung: string | undefined;
    rang_buoc_text: string | undefined;
    hinh_thuc_dang_ky: number | undefined;
    hinh_thuc_dang_ky_text: string | undefined;
    tinh_chat_lop_text: string | undefined;
    so_tin_chi: number;
    classes: IClassListItem[];
}

/** Backend trả `thu` 2-7, CN = 8. UI hiển thị "2".."7" hoặc "CN". */
export const formatThu = (thu: number): string => {
    if (thu === 8) return "CN";
    return (thu + 2).toString();
};

/** Format tiết: backend trả `tiet` (start, 0-based) + `so_tiet` (length). UI hiển thị "5 - 9". */
export const formatTiet = (tiet: number | undefined, soTiet: number | undefined): string => {
    if (tiet === undefined || tiet === null) return "—";
    const length = soTiet && soTiet > 0 ? soTiet : 1;
    return length > 1 ? `${tiet + 1} - ${tiet + length}` : `${tiet + 1}`;
};

/** Format ISO datetime → "HH:mm dd/MM/yyyy" theo locale vi-VN. */
export const formatDateVi = (iso: string | null | undefined): string => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString("vi-VN", {
        hour: "2-digit",
        minute: "2-digit",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    });
};

/** Format khoảng thời gian còn lại (ms) → "2 ngày 3 giờ 15 phút 30 giây".
 *  Bỏ qua đơn vị 0 ở đầu; luôn hiển thị giây để countdown chuyển động liên tục. */
export const formatCountdown = (ms: number): string => {
    if (ms <= 0) return "0 giây";
    const totalSec = Math.floor(ms / 1000);
    const days = Math.floor(totalSec / 86400);
    const hours = Math.floor((totalSec % 86400) / 3600);
    const minutes = Math.floor((totalSec % 3600) / 60);
    const seconds = totalSec % 60;
    const parts: string[] = [];
    if (days > 0) parts.push(`${days} ngày`);
    if (hours > 0 || days > 0) parts.push(`${hours} giờ`);
    if (minutes > 0 || hours > 0 || days > 0) parts.push(`${minutes} phút`);
    parts.push(`${seconds} giây`);
    return parts.join(" ");
};

/** ISO datetime → "DD/MM HH:mm". Nếu không parse được (server trả sẵn chuỗi hiển thị) → trả nguyên.
 *  Cùng logic với `formatTime` của Header để giờ đợt hiển thị nhất quán giữa Header và banner. */
export const formatClock = (value?: string | null): string => {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const HH = String(d.getHours()).padStart(2, "0");
    const MI = String(d.getMinutes()).padStart(2, "0");
    return `${dd}/${mm} ${HH}:${MI}`;
};

/**
 * FE gate tài chính — mirror backend EligibilityService rule strict mode.
 * Trả về string mô tả lý do block (để hiển thị thay nút Đăng ký), hoặc null khi cho phép.
 *
 * Cờ đợt off → null (không gate).
 * Cờ on + lớp có TC nhưng so_tien = 0 → đơn giá học phí chưa cấu hình trong dmMucHocPhiTinChi
 *   → "Chưa có đơn giá" (mirror BE MISSING_TUITION_RATE).
 * Lớp có TC = 0 (môn không tính phí — Quân sự / Thể dục một số trường) → null cho qua.
 * Cờ on + lớp có đơn giá:
 *   - SV không có hồ sơ tài chính (`!co_so_du`) → "Chưa có hồ sơ TC".
 *   - Tổng đã đăng ký + lớp này > số dư → "Không đủ số dư".
 *
 * Backend vẫn enforce ở EligibilityService — đây chỉ là FE preempt cho UX, tránh SV bấm xong
 * mới biết bị từ chối. Single source of truth: dùng chung cho ClassesPage desktop + ClassCard mobile.
 */
export function getFinancialBlock(c: IClassListItem, financial: IFinancialSummary | null): string | null {
    if (!financial?.tinh_hoc_phi_theo_so_du) return null;
    const tc = c.so_tin_chi ?? 0;
    const tien = c.so_tien ?? 0;
    const phai = c.so_tien_phai_nop ?? 0;
    // TC > 0 nhưng so_tien = 0 → đơn giá thiếu cấu hình hoặc tính chất không quy đổi được.
    // Chặn cứng vì backend cũng reject (MISSING_TUITION_RATE).
    if (tc > 0 && tien === 0) return "Chưa có đơn giá";
    // Sau bước trên: hoặc TC=0 (môn miễn phí valid) hoặc tien>0. phai=0 do miễn giảm 100% → cho qua.
    if (phai <= 0) return null;
    if (!financial.co_so_du) return "Chưa có hồ sơ TC";
    if (financial.tong_tien_phai_nop + phai > financial.so_du) return "Không đủ số dư";
    return null;
}

/** Lý do nút đăng ký / Chọn LTH-SH bị khóa — hiển thị qua tooltip (backend: message + reasonCode).
 *  <c>gateReason</c>: ngoài [openAt, closeAt], hoặc queue/status chưa sẵn sàng (đồng bộ thời gian cổng). */
export function registerBlockedTooltip(item: IClassListItem, isFull: boolean, gateReason?: string): string {
    if (gateReason) return gateReason;
    const m = item.message?.trim();
    if (m) return m;
    if (isFull) return "Lớp đã hết chỗ đăng ký.";
    switch (item.reasonCode) {
        case "CLASS_FULL":
            return "Lớp đã hết chỗ đăng ký.";
        case "OUT_OF_PHAM_VI":
            return "Không thuộc phạm vi đăng ký.";
        case "RANG_BUOC_FAIL":
            return item.rang_buoc_text?.trim()
                ? `Chưa đủ điều kiện ràng buộc (${item.rang_buoc_text}).`
                : "Chưa đủ điều kiện ràng buộc môn học.";
        case "ALREADY_REGISTERED":
            return "Bạn đã đăng ký lớp này.";
        case "ALREADY_REGISTERED_SAME_MON":
            return "Đã đăng ký lớp khác cùng môn trong đợt này.";
        case "SCHEDULE_CONFLICT":
            return "Trùng lịch với lớp đã đăng ký hoặc lớp khác.";
        default:
            return item.reasonCode
                ? `Không thể đăng ký (${item.reasonCode}).`
                : "Không thể đăng ký lớp này.";
    }
}
